// B10 Trip book (mockup docs/renders/trip-book tb01–tb04): the end-of-trip recap, built from what
// the app already keeps per trip. A cover (the trip, its islands, who came, four totals), a page per
// stage (the places found there, the acts opened on its island, the collectibles from its set, the
// holiday tasks done while there) and a last page (who found what, the best moments: the rare acts).
// Pure: every event names its trip (D1; older events belong to the first trip).
import type { Pack } from '../../shared/pack/schema'
import { PrizeWon, RewardPaid, STARTER_TASKS, TaskDone } from '../../shared/rewards'
import { collectible, collectibleSet } from './collectibles'
import { ACT_CATALOG } from '../map/places/actCatalog'
import { placeId } from '../map/places/registry'

interface Ev {
  id: string
  type: string
  at: number
  personId: string
  tripId?: string
  payload: unknown
}

export interface BookPerson {
  id: string
  name: string
}

export interface BookAct {
  place: string
  act: string
  label: string
  rarity: 'common' | 'rare' | 'legendary'
  by: string
}

export interface BookPage {
  stageId: string
  name: string
  start: string
  end: string
  days: number
  place: string
  /** Places checked in at (near) during the stage, by anyone; how many check-ins. */
  found: string[]
  checkins: number
  acts: BookAct[]
  collectibles: { id: string; name: string; emoji: string; art?: string }[]
  tasks: string[]
}

export interface PersonTotals {
  id: string
  name: string
  places: number
  acts: number
  collectibles: number
  words: number
  tasks: number
  /** Their rarest act opened, for "spotted the yeti". */
  best?: string
}

export interface TripBook {
  title: string
  start: string
  end: string
  islands: number
  people: BookPerson[]
  totals: { places: number; acts: number; rare: number; words: number; collectibles: number; prizes: number }
  pages: BookPage[]
  who: PersonTotals[]
  /** The rare and legendary acts opened on the trip, rarest first. */
  best: BookAct[]
}

const days = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000) + 1
const dateOf = (ms: number, tz: string) => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(ms)
const RANK = { legendary: 0, rare: 1, common: 2 } as const

export function tripBook(events: readonly Ev[], tripId: string, firstTrip: string, pack: Pick<Pack, 'meta' | 'itinerary' | 'places'>, people: readonly BookPerson[]): TripBook {
  const tz = pack.meta.timezone
  const mine = events.filter((e) => (e.tripId ?? firstTrip) === tripId)
  const placeName = new Map(pack.places.map((p) => [p.id, p.name]))
  const players = people.filter((p) => mine.some((e) => e.personId === p.id))

  const checkins = mine.filter((e) => e.type === 'checkin' && (e.payload as { near?: boolean } | null)?.near)
  const paid = mine.flatMap((e) => {
    if (e.type !== 'reward.paid') return []
    const p = RewardPaid.safeParse(e.payload)
    return p.success && p.data.for === e.personId ? [{ ...p.data, at: e.at }] : []
  })
  const actOf = (place: string, act: string, by: string): BookAct | null => {
    const a = ACT_CATALOG[place]?.find((x) => x.id === act)
    return a ? { place, act, label: a.label, rarity: a.rarity, by } : null
  }
  const acts = paid.flatMap((p) => {
    const a = p.kind === 'key' && p.place && p.act ? actOf(p.place, p.act, p.for) : null
    return a ? [a] : []
  })
  const tasks = mine.flatMap((e) => {
    if (e.type !== 'task.done') return []
    const p = TaskDone.safeParse(e.payload)
    return p.success ? [{ ...p.data, at: e.at }] : []
  })
  const taskTitle = (id: string) => {
    const set = [...mine].filter((e) => e.type === 'task.set' && (e.payload as { taskId?: string }).taskId === id).at(-1)
    return (set?.payload as { fields?: { title?: string } } | undefined)?.fields?.title ?? STARTER_TASKS[id]?.title ?? id.replace(/-/g, ' ')
  }

  const pages: BookPage[] = pack.itinerary.stages.map((s) => {
    const inStage = (ms: number) => {
      // B13: a stage's dates are in its own zone.
      const d = dateOf(ms, s.timezone ?? tz)
      return d >= s.startDate && d <= s.endDate
    }
    const here = checkins.filter((e) => inStage(e.at))
    const place = placeId(s.spot)
    const setIds = new Set(collectibleSet(place).map((c) => c.id))
    const got = [...new Set(paid.filter((p) => p.kind === 'collectible' && p.item && setIds.has(p.item)).map((p) => p.item!))]
    return {
      stageId: s.id,
      name: s.name,
      start: s.startDate,
      end: s.endDate,
      days: days(s.startDate, s.endDate),
      place,
      found: [...new Set(here.map((e) => placeName.get((e.payload as { placeId: string }).placeId) ?? (e.payload as { placeId: string }).placeId))],
      checkins: here.length,
      acts: acts.filter((a) => a.place === place).sort((a, b) => RANK[a.rarity] - RANK[b.rarity]),
      collectibles: got.flatMap((id) => {
        const c = collectible(id)
        return c ? [{ id, name: c.name, emoji: c.emoji, ...(c.art ? { art: c.art } : {}) }] : []
      }),
      tasks: [...new Set(tasks.filter((t) => t.day >= s.startDate && t.day <= s.endDate).map((t) => taskTitle(t.taskId)))],
    }
  })

  // Words: cards a player answered right during this trip (from its first event to its last day).
  const from = Math.min(...mine.map((e) => e.at), Date.parse(`${pack.meta.startDate}T00:00:00Z`))
  const to = Date.parse(`${pack.meta.endDate}T23:59:59Z`) + 86_400_000
  const words = (id: string) => new Set(events.filter((e) => e.type === 'card.answered' && e.personId === id && e.at >= from && e.at <= to && (e.payload as { result?: string }).result === 'right').map((e) => (e.payload as { cardId: string }).cardId)).size

  const who: PersonTotals[] = players.map((p) => {
    const theirActs = acts.filter((a) => a.by === p.id).sort((a, b) => RANK[a.rarity] - RANK[b.rarity])
    return {
      id: p.id,
      name: p.name,
      places: new Set(checkins.filter((e) => e.personId === p.id).map((e) => (e.payload as { placeId: string }).placeId)).size,
      acts: theirActs.length,
      collectibles: new Set(paid.filter((x) => x.kind === 'collectible' && x.for === p.id).map((x) => x.item)).size,
      words: words(p.id),
      tasks: tasks.filter((t) => t.for === p.id).length,
      ...(theirActs[0] && theirActs[0].rarity !== 'common' ? { best: theirActs[0].label } : {}),
    }
  })

  const best = acts.filter((a) => a.rarity !== 'common').sort((a, b) => RANK[a.rarity] - RANK[b.rarity])
  const uniqueBest = best.filter((a, i) => best.findIndex((b) => b.place === a.place && b.act === a.act) === i)
  return {
    title: pack.meta.title,
    start: pack.meta.startDate,
    end: pack.meta.endDate,
    islands: new Set(pack.itinerary.stages.map((s) => placeId(s.spot))).size,
    people: players,
    totals: {
      places: new Set(checkins.map((e) => (e.payload as { placeId: string }).placeId)).size,
      acts: new Set(acts.map((a) => `${a.place}:${a.act}`)).size,
      rare: new Set(best.map((a) => `${a.place}:${a.act}`)).size,
      words: who.reduce((s, p) => s + p.words, 0),
      collectibles: new Set(paid.filter((p) => p.kind === 'collectible').map((p) => `${p.for}|${p.item}`)).size,
      prizes: mine.filter((e) => e.type === 'prize.won' && PrizeWon.safeParse(e.payload).success).length,
    },
    pages,
    who,
    best: uniqueBest.slice(0, 4),
  }
}
